import { useState } from 'react';
import { Button } from '../../components/ui/button';

interface Attorney {
  id: string;
  name: string;
  title: string;
  imageUrl: string;
  shortBio: string;
  fullBio: string;
  specialties: string[];
}

const attorneys: Attorney[] = [
  {
    id: 'paul-ayerbe',
    name: 'Paul Ayerbe',
    title: 'Senior Partner',
    imageUrl: 'https://aainjury.com/wp-content/uploads/2025/09/AREYIBE-731x1024-1.jpeg',
    shortBio: 'Over 30 years of experience in personal injury law with multi-million dollar verdicts.',
    fullBio: `Paul Ayerbe focuses his practice solely on personal injury, tractor-trailer accidents, wrongful death, and auto accident cases. His practice has secured many high-value results for his clients, representing cases throughout the State of Georgia.

Mr. Ayerbe was born outside of New York City to an immigrant family. He learned the value of hard work from his father who came to the United States with nothing and retired as director of languages at the University of Illinois at Chicago.

Mr. Ayerbe attended Syracuse University where he was admitted into the honors program. He graduated with a degree in International Relations and a minor in Spanish. Mr. Ayerbe entered the Army as an officer in 1986 and continued his military career after he entered law school through the reserves. He graduated from the Walter F. George School of Law at Mercer University in 1992 after taking a year off to serve in Desert Storm.

During law school Mr. Ayerbe was chosen to represent Mercer University on its Moot Court Team that participated in national competitions. After graduating from Mercer University with a Juris Doctorate, Mr. Ayerbe joined the law firm of Lovett, Cowart and Ayerbe.

Mr. Ayerbe has obtained several multi-million-dollar verdicts and settlements including one of Georgia's top verdicts for 2010 as well as record breaking results in both rural and urban counties in Georgia. Mr. Ayerbe has obtained a 4.2 million dollar recovery in the Federal Middle District of Georgia.

He is a past President of the Middle Georgia Trial Lawyer Association and has been chosen to participate in local hospital senior community leader programs. Mr. Ayerbe was recently awarded the prestigious Super Lawyer designation which is given to less than 5% of all trial lawyers.`,
    specialties: ['Personal Injury', 'Tractor-Trailer Accidents', 'Wrongful Death', 'Auto Accidents'],
  },
  {
    id: 'william-arnold',
    name: 'William "Bill" Arnold',
    title: 'Partner',
    imageUrl: 'https://aainjury.com/wp-content/uploads/2025/09/untitledshoot-9698-791x1024-1.jpeg',
    shortBio: 'Mercer Law graduate dedicated to serving the Middle Georgia community.',
    fullBio: `Bill Arnold was born in Augusta, GA and moved to Macon at the age of four after his father finished medical school. After seeing his dad serve the Middle Georgia community in his medical practice for over twenty years, Bill decided he wanted to do the same. He decided he could best serve the Middle Georgia community through the practice of law. He credits his hard work and dedication to his father.

He attended college at Mercer University where he made Dean's list and was also a member of the History Honor's Society. He then went on to attend Law School at Mercer University School of Law where he graduated in 2008.

His practice focuses primarily on personal injury litigation. Throughout that time, he has handled numerous cases involving serious injuries to his clients. He has had successful results through trial verdicts, mediation, and out of court settlements.

He is excited to live in Macon and looks forward to serving the Middle Georgia Community for years to come.`,
    specialties: ['Personal Injury Litigation', 'Trial Verdicts', 'Mediation', 'Settlements'],
  },
  {
    id: 'antonio-solomon',
    name: 'Antonio Solomon',
    title: 'Associate Attorney',
    imageUrl: 'https://aainjury.com/wp-content/uploads/2025/09/untitledshoot-9714.jpeg',
    shortBio: 'Passionate advocate for fairness and equal opportunities.',
    fullBio: `I am thrilled to have this opportunity to introduce myself. I come from the charming town of Macon, Georgia, which may be small but holds a very special place in my heart. However, my childhood was mainly in Roberta.

As much as I loved my hometowns, the prospect of exploring the cosmopolitan life of Atlanta became irresistible to me, and so I moved here for my schooling years.

Fairness and equal opportunities are values that have always been embedded in my veins. I owe a lot of this to my family, who instilled in me from a young age the belief that education is the key to success; this helped a great deal in my academic achievements in both undergraduate and law school.

I take a great deal of pleasure in looking back at historical events and reflecting on how they have influenced the world we live in today. Outside of my academic interests, I have an uncontainable love for nature and outdoor activities.

My twins, Blake and Blaire, are the apple of my eye, and I cherish every moment I spend with them.`,
    specialties: ['Personal Injury', 'Civil Litigation'],
  },
];

function AttorneyImage({ uri, name, className }: { uri: string; name: string; className?: string }) {
  const [hasError, setHasError] = useState(false);
  const initials = name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();

  if (hasError) {
    return (
      <div className={`bg-aalaw-red flex items-center justify-center ${className}`}>
        <span className="text-white font-bold text-2xl">{initials}</span>
      </div>
    );
  }

  return (
    <img
      src={uri}
      alt={name}
      className={`object-cover ${className}`}
      onError={() => setHasError(true)}
    />
  );
}

function AttorneyModal({ attorney, onClose }: { attorney: Attorney; onClose: () => void }) {
  return (
    <div className="fixed inset-0 z-50 flex items-end sm:items-center justify-center p-4">
      <div className="absolute inset-0 bg-black/50" onClick={onClose} />
      <div className="relative bg-white w-full max-w-2xl max-h-[85vh] rounded-xl overflow-hidden flex flex-col">
        <div className="flex items-center justify-between p-4 border-b border-gray-200 flex-shrink-0">
          <h2 className="text-lg font-semibold text-gray-900">Attorney Profile</h2>
          <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-full transition-colors" aria-label="Close">
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
        <div className="flex-1 overflow-y-auto p-6">
          <div className="flex flex-col items-center text-center mb-6">
            <AttorneyImage uri={attorney.imageUrl} name={attorney.name} className="w-36 h-44 rounded-xl mb-4" />
            <h3 className="text-xl font-bold text-gray-900">{attorney.name}</h3>
            <p className="text-aalaw-red font-medium">{attorney.title}</p>
          </div>
          <div className="mb-6">
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Practice Areas</h4>
            <div className="flex flex-wrap gap-2">
              {attorney.specialties.map((specialty, index) => (
                <span key={index} className="px-3 py-1 bg-gray-100 text-gray-600 text-sm rounded-full">{specialty}</span>
              ))}
            </div>
          </div>
          <div>
            <h4 className="text-sm font-semibold text-gray-900 mb-3">Biography</h4>
            <div className="text-sm text-gray-600 leading-relaxed whitespace-pre-line">{attorney.fullBio}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AboutUsScreen() {
  const [selectedAttorney, setSelectedAttorney] = useState<Attorney | null>(null);

  const handleCallOffice = () => window.location.href = 'tel:4787452222';
  const handleVisitWebsite = () => window.open('https://aainjury.com', '_blank', 'noopener,noreferrer');
  const handleEmail = () => window.location.href = 'mailto:support@aalaw.com';

  return (
    <div className="bg-white sm:rounded-xl shadow-sm sm:border border-gray-200 h-[calc(100vh-8rem)] sm:h-[calc(100vh-10rem)] min-h-[300px] flex flex-col overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 border-b border-gray-200 px-4 sm:px-6 py-4">
        <h1 className="text-xl font-bold text-gray-900">About Us</h1>
        <p className="text-sm text-gray-500">Learn about AA Law and our team</p>
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto overscroll-contain scrollbar-visible" style={{ WebkitOverflowScrolling: 'touch' }}>
        <div className="max-w-2xl mx-auto px-4 sm:px-6 py-6 space-y-6">
          {/* Firm Overview */}
          <div className="bg-gray-50 rounded-xl p-6 text-center">
            <img src="/icon.png" alt="AA Law Logo" className="w-20 h-20 mx-auto mb-4 rounded-xl object-contain" />
            <h2 className="text-xl font-bold text-gray-900 mb-1">AA Law</h2>
            <p className="text-gray-500 text-sm mb-5">Fighting for Justice Since 1992</p>

            {/* Stats */}
            <div className="flex items-center justify-around py-4 border-y border-gray-200 mb-5">
              <div className="text-center">
                <p className="text-lg font-bold text-aalaw-red">$100M+</p>
                <p className="text-xs text-gray-500">Recovered</p>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div className="text-center">
                <p className="text-lg font-bold text-aalaw-red">50+</p>
                <p className="text-xs text-gray-500">Years Combined</p>
              </div>
              <div className="w-px h-8 bg-gray-200" />
              <div className="text-center">
                <p className="text-lg font-bold text-aalaw-red">1000s</p>
                <p className="text-xs text-gray-500">Clients Helped</p>
              </div>
            </div>

            <p className="text-sm text-gray-600 leading-relaxed mb-4">
              At AA Law, we stand up for individuals and families against the corporations 
              or institutions that try to take advantage of them. Our team leaves no stone 
              unturned in our fight for proper compensation.
            </p>

            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-4 py-2 rounded-lg">
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
              <span className="text-sm font-medium">The fee is free unless we win!</span>
            </div>
          </div>

          {/* Meet Our Attorneys */}
          <div>
            <h3 className="text-base font-semibold text-gray-900 mb-3">Meet Our Attorneys</h3>
            <div className="space-y-3">
              {attorneys.map((attorney) => (
                <button
                  key={attorney.id}
                  onClick={() => setSelectedAttorney(attorney)}
                  className="w-full text-left bg-gray-50 hover:bg-gray-100 rounded-xl p-4 flex gap-4 transition-colors"
                >
                  <AttorneyImage uri={attorney.imageUrl} name={attorney.name} className="w-20 h-24 rounded-lg flex-shrink-0" />
                  <div className="flex-1 min-w-0">
                    <h4 className="font-semibold text-gray-900">{attorney.name}</h4>
                    <p className="text-sm text-aalaw-red font-medium mb-1">{attorney.title}</p>
                    <p className="text-sm text-gray-600 line-clamp-2">{attorney.shortBio}</p>
                  </div>
                  <svg className="w-5 h-5 text-gray-400 flex-shrink-0 self-center" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              ))}
            </div>
          </div>

          {/* Contact Section */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="text-base font-semibold text-gray-900 text-center mb-1">Get in Touch</h3>
            <p className="text-sm text-gray-500 text-center mb-4">Contact us today for a free conference.</p>

            <div className="space-y-2 mb-5">
              <Button onClick={handleCallOffice} className="w-full bg-aalaw-red hover:bg-aalaw-red-dark">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z" />
                </svg>
                Call (478) 745-2222
              </Button>
              <Button variant="outline" onClick={handleVisitWebsite} className="w-full">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 12a9 9 0 01-9 9m9-9a9 9 0 00-9-9m9 9H3m9 9a9 9 0 01-9-9m9 9c1.657 0 3-4.03 3-9s-1.343-9-3-9m0 18c-1.657 0-3-4.03-3-9s1.343-9 3-9m-9 9a9 9 0 019-9" />
                </svg>
                Visit aainjury.com
              </Button>
              <Button variant="outline" onClick={handleEmail} className="w-full">
                <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
                </svg>
                Email support@aalaw.com
              </Button>
            </div>

            <div className="flex items-start gap-3 pt-4 border-t border-gray-200">
              <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
              </svg>
              <div className="text-sm text-gray-600">
                <p>577 Mulberry Street, Suite 1000</p>
                <p>Macon, GA 31201</p>
              </div>
            </div>

            <div className="flex items-start gap-3 mt-3">
              <svg className="w-5 h-5 text-gray-400 mt-0.5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
              <div className="text-sm text-gray-600">
                <p>Mon - Fri: 8:30 AM - 5:00 PM</p>
                <p className="text-aalaw-red">24/7 Emergency Line Available</p>
              </div>
            </div>
          </div>

          {/* Practice Areas */}
          <div className="bg-gray-50 rounded-xl p-5">
            <h3 className="text-base font-semibold text-gray-900 mb-3">Practice Areas</h3>
            <div className="grid grid-cols-2 gap-2">
              {['Personal Injury', 'Auto Accidents', 'Truck Accidents', 'Wrongful Death', 'Slip & Fall', 'Medical Malpractice', 'Product Liability', 'Workers Compensation'].map((area) => (
                <div key={area} className="flex items-center gap-2 text-sm text-gray-700">
                  <svg className="w-4 h-4 text-aalaw-red flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                  </svg>
                  <span>{area}</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>

      {selectedAttorney && <AttorneyModal attorney={selectedAttorney} onClose={() => setSelectedAttorney(null)} />}
    </div>
  );
}
